import { CSSProperties, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Logger from "Logger";
import PostAuthRoutes from "./PostAuthRoutes";
import useCurrentUser from 'Hooks/useCurrentUser';
import useOrganization from 'Hooks/useOrganization';
import useCurrentBrand from 'Hooks/useCurrentBrand';
import { useHistory } from "react-router-dom";
import Navbar from "components/Navbar/Navbar";
import UpgradeModalProvider from "Hooks/UpgradeModalContext";
import NavbarProvider from "pages/MarketingPage/Data/NavbarProvider";
import AlertContext from "Hooks/AlertContext";
import useScreen from "Hooks/useScreen";
import LoaderOverlay from "elements/LoaderOverlay";

const InitializeApp = () => {
    const history = useHistory();
    const {
        isAuthenticated: isAuthenticatedWithAuth0,
        isLoading: isAuth0Loading,
        logout,
        loginWithRedirect: redirectToAuth0LoginPage,
        getAccessTokenSilently,
        user: userAuth0,
    } = useAuth0();

    const {
        currentUser,
        getCurrentUser,
        isLoadingUser,
    } = useCurrentUser();

    const {
        currentBrand,
        getBrands,
        isLoadingBrands,
        loadedBrands,
    } = useCurrentBrand();

    const {
        organization,
        isOrganizationInitialized,
        getOrganization
    } = useOrganization();

    const { cssCalc } = useScreen();

    /** Initializes the user session with Auth0 and database details */
    const initializeUserSession = async () => {
        // console.log('initializeUserSession >>>>');
        try {
            // Wait for Auth0 to finish loading before making any decisions
            if (isAuth0Loading) {
                return;
            }

            // Only redirect if we're definitely not authenticated
            if (!isAuthenticatedWithAuth0) {
                // console.log('initializeUserSession >>>> not authenticated');
                const params = new URLSearchParams(window.location.search);
                const authCode = params.get('auth_code');

                // If we have a TikTok auth code, prioritize redirecting to the TikTok callback
                if (authCode) {
                    // Store auth_code in localStorage for the callback route to use
                    localStorage.setItem('tt_auth_code', authCode);

                }

                redirectToAuth0LoginPage({
                    redirectUri: `${window.location.origin}`
                });
                return;
            }

            // Add guards against re-initialization
            const userSubAuth0 = userAuth0?.sub;
            if (!userSubAuth0) {
                // console.log('initializeUserSession >>>> isLoadingUser || currentUser');
                return;
            }

            await getCurrentUser(
                true,
                userSubAuth0,
                getAccessTokenSilently,
                logout,
            );
            await getBrands(true);
            await getOrganization(currentBrand?.organizationId);
        } catch (err) {
            Logger.error('Error initializing session:', err);
            if (!isAuthenticatedWithAuth0) {
                logout();
            }
        }
    };

    useEffect(() => {
        if (!isAuth0Loading) {
            initializeUserSession();
        }
    }, [isAuth0Loading, isAuthenticatedWithAuth0, logout]);

    useEffect(() => {
        if (!currentUser || !loadedBrands) {
            return;
        }

        if (!currentUser.email) {
            history.push('/email');
            return;
        }

        if (!loadedBrands.length) {
            history.push('/add-artist');
            return;
        }

        if (organization?.status === 'waiting_for_details') {
            history.push('/team');
        }
    }, [currentUser, loadedBrands, organization]);

    const debugData = {
        isAuth0Loading,
        isLoadingUser,
        isAuthenticatedWithAuth0,
        isLoadingBrands,
        userAuth0,
        organization,
        currentUser,
        loadedBrands,
        currentBrand,
    }

    const renderLoader = () => {
        return (
            <LoaderOverlay
                className="loaderFullScreenInitializeApp1"
                title=""
            >
                <div style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 20, marginTop: 10 }}>
                    Welcome to Symphony
                </div>
            </LoaderOverlay>
        )
    }

    const renderMainApp = () => {
        return (
            <div
                className="initializeAppComponent"
                style={{
                    ...sty.initializeAppComponent,
                    height: cssCalc()
                }}
            >
                <UpgradeModalProvider>
                    <AlertContext>
                        <NavbarProvider>
                            <Navbar />
                            <PostAuthRoutes />
                        </NavbarProvider>
                    </AlertContext>
                </UpgradeModalProvider>
            </div>
        )
    }

    if (currentUser && loadedBrands && isOrganizationInitialized) {
        return renderMainApp()
    }

    return renderLoader();
};

const sty: Record<string, CSSProperties> = {
    initializeAppComponent: {
        display: "flex",
        width: "100%",
    }
}

// const InitializeApp = () => {
//     return <div>InitializeApp</div>;
// }

export default InitializeApp;